import { useCallback, useEffect, useState } from 'react';
import { alertEngine, requestNotificationPermission, type Alert } from '../data/alerts';
import { store } from '../data/store';
import { hms, pct, price as fmtPrice } from '../lib/num';
import { useStoreTick, useStructure } from './hooks';

/**
 * Price alerts.
 *
 * Alerts are armed against a crossing, so the panel shows the distance to
 * target as a live percentage — the number that tells you whether an alert is
 * about to fire or is parked 30% away and effectively forgotten.
 */
export function AlertsPanel() {
  useStructure();
  useStoreTick();

  const [, force] = useState(0);
  const [priceInput, setPriceInput] = useState('');

  useEffect(() => alertEngine.changed.on(() => force((n) => n + 1)), []);

  const symbol = store.active;
  const st = store.get(symbol);
  const current = st.ticker.last;

  const submit = useCallback(() => {
    const v = Number(priceInput);
    if (!Number.isFinite(v) || v <= 0) return;
    requestNotificationPermission();
    alertEngine.add(symbol, v);
    setPriceInput('');
  }, [priceInput, symbol]);

  const alerts = alertEngine.list();
  const armed = alerts.filter((a) => a.status === 'armed');
  const done = alerts.filter((a) => a.status === 'triggered');

  return (
    <div className="panel">
      <div className="panel-head">
        <span className="panel-title">Alerts</span>
        <span className="sub">
          {armed.length} armed{done.length > 0 ? ` · ${done.length} fired` : ''}
        </span>
        <span className="spacer" />
        {done.length > 0 && (
          <button
            className="btn sm ghost"
            onClick={() => alertEngine.clearTriggered()}
            title="Remove triggered alerts"
          >
            clear fired
          </button>
        )}
      </div>

      <div className="alert-add">
        <span className="alert-sym">{symbol}</span>
        <input
          className="input num"
          type="number"
          step="any"
          placeholder={Number.isFinite(current) ? fmtPrice(current, st.pricePrecision) : 'price'}
          value={priceInput}
          onChange={(e) => setPriceInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && submit()}
          title="Direction is inferred from where this sits versus the market"
        />
        <button className="btn" onClick={submit} disabled={!priceInput}>
          Arm
        </button>
      </div>

      <div className="panel-body alert-list">
        {alerts.length === 0 && (
          <div className="placeholder" style={{ position: 'static', padding: '18px 8px' }}>
            no alerts — alt-click the chart to add one
          </div>
        )}
        {alerts.map((a) => (
          <AlertRow key={a.id} alert={a} />
        ))}
      </div>
    </div>
  );
}

function AlertRow({ alert: a }: { alert: Alert }) {
  const st = store.peek(a.symbol);
  const current = st?.ticker.last ?? alertEngine.priceOf(a.symbol) ?? NaN;
  const distance = Number.isFinite(current) && current > 0 ? ((a.price - current) / current) * 100 : NaN;
  const near = Number.isFinite(distance) && Math.abs(distance) < 0.5;

  return (
    <div className={`alert-row ${a.status}`}>
      <span
        className={`alert-dir ${a.direction === 'above' ? 'up' : 'down'}`}
        title={`Fires when ${a.symbol} crosses ${a.direction} ${a.price}`}
      >
        {a.direction === 'above' ? '▲' : '▼'}
      </span>

      <button
        className="alert-symbol"
        onClick={() => store.setActive(a.symbol)}
        title="Focus this symbol"
      >
        {a.symbol.replace(/USDT$/, '')}
      </button>

      <span className="alert-price num">{fmtPrice(a.price, st?.pricePrecision ?? 2)}</span>

      {a.status === 'armed' ? (
        <span className={`alert-dist num ${near ? 'accent' : 'muted'}`} title="Distance to target">
          {Number.isFinite(distance) ? pct(distance, 2) : '—'}
        </span>
      ) : (
        <span className="alert-dist muted" title={`Fired at ${a.triggeredPrice}`}>
          {a.triggeredAt ? hms(a.triggeredAt) : 'fired'}
        </span>
      )}

      <button
        className={`alert-flag ${a.repeat ? 'on' : ''}`}
        onClick={() => alertEngine.update(a.id, { repeat: !a.repeat })}
        title="Re-arm automatically after firing"
      >
        ↻
      </button>
      <button
        className={`alert-flag ${a.sound ? 'on' : ''}`}
        onClick={() => alertEngine.update(a.id, { sound: !a.sound })}
        title="Play a chime when this fires"
      >
        ♪
      </button>
      {a.status === 'triggered' && (
        <button className="alert-flag" onClick={() => alertEngine.rearm(a.id)} title="Re-arm">
          ⏻
        </button>
      )}
      <button
        className="alert-flag danger"
        onClick={() => alertEngine.remove(a.id)}
        title="Delete alert"
      >
        ×
      </button>
    </div>
  );
}
